import React, { useState, useEffect } from "react";
import "./GuestbookManagement.css";
import {
  getGuestbookEntries,
  deleteGuestbookEntry,
  GuestbookEntry as SupabaseGuestbookEntry,
} from "../supabase";

interface GuestbookEntry {
  id: string;
  name: string;
  message: string;
  side: "groom" | "bride" | "none";
  createdAt: string;
}

const GuestbookManagement: React.FC = () => {
  const [entries, setEntries] = useState<GuestbookEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "groom" | "bride" | "none">(
    "all"
  );

  useEffect(() => {
    // Supabase에서 축하한마디 데이터 가져오기
    const fetchEntries = async () => {
      try {
        setLoading(true);
        const supabaseEntries = await getGuestbookEntries();
        const formattedEntries: GuestbookEntry[] = supabaseEntries.map(
          (entry) => ({
            id: entry.id || "",
            name: entry.name,
            message: entry.message,
            side: entry.side,
            createdAt: entry.created_at || new Date().toISOString(),
          })
        );
        setEntries(formattedEntries);
      } catch (error) {
        console.error("Error fetching guestbook entries:", error);
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEntries();
  }, []);

  const handleDelete = async (id: string) => {
    if (window.confirm("정말로 이 메시지를 삭제하시겠습니까?")) {
      try {
        // Supabase에서 축하한마디 삭제
        await deleteGuestbookEntry(id);
        setEntries((prev) => prev.filter((entry) => entry.id !== id));
        alert("메시지가 삭제되었습니다.");
      } catch (error) {
        console.error("Error deleting entry:", error);
        alert("삭제에 실패했습니다. 다시 시도해주세요.");
      }
    }
  };

  const filteredEntries = entries.filter((entry) => {
    if (filter === "all") return true;
    return entry.side === filter;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR", {
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return <div className="guestbook-management loading">로딩 중...</div>;
  }

  return (
    <div className="guestbook-management">
      <div className="guestbook-management-header">
        <h2>축하한마디 관리</h2>
        <p>방명록 메시지를 관리할 수 있습니다</p>
      </div>

      <div className="guestbook-management-content">
        <div className="filter-section">
          <h3>필터</h3>
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filter === "all" ? "active" : ""}`}
              onClick={() => setFilter("all")}
            >
              전체 ({entries.length})
            </button>
            <button
              className={`filter-btn ${filter === "groom" ? "active" : ""}`}
              onClick={() => setFilter("groom")}
            >
              신랑측 ({entries.filter((e) => e.side === "groom").length})
            </button>
            <button
              className={`filter-btn ${filter === "bride" ? "active" : ""}`}
              onClick={() => setFilter("bride")}
            >
              신부측 ({entries.filter((e) => e.side === "bride").length})
            </button>
            <button
              className={`filter-btn ${filter === "none" ? "active" : ""}`}
              onClick={() => setFilter("none")}
            >
              미분류 ({entries.filter((e) => e.side === "none").length})
            </button>
          </div>
        </div>

        <div className="entries-section">
          <h3>메시지 목록</h3>
          {filteredEntries.length === 0 ? (
            <div className="no-entries">
              {filter === "all"
                ? "아직 메시지가 없습니다."
                : `${
                    filter === "groom"
                      ? "신랑"
                      : filter === "bride"
                      ? "신부"
                      : "미분류"
                  }${filter === "none" ? "" : "측"} 메시지가 없습니다.`}
            </div>
          ) : (
            <div className="entries-list">
              {filteredEntries.map((entry) => (
                <div key={entry.id} className={`entry-card ${entry.side}`}>
                  <div className="entry-header">
                    <div className="entry-info">
                      {entry.side !== "none" && (
                        <span className={`entry-side ${entry.side}`}>
                          <span style={{ fontSize: "0.85rem", marginRight: 0 }}>
                            {entry.side === "groom" ? "🤵‍♂️" : "👰‍♀️"}
                          </span>
                          <span style={{ fontSize: "0.7rem", marginLeft: 1 }}>
                            {entry.side === "groom" ? "신랑측" : "신부측"}
                          </span>
                        </span>
                      )}
                      <span className="entry-name">{entry.name}</span>
                    </div>
                    <div className="entry-actions">
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(entry.id)}
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                  <div className="entry-message">{entry.message}</div>
                  <div className="entry-date">
                    {formatDate(entry.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestbookManagement;
